import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { getTeamImageById } from '@/helpers/images-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';
import { Rutas } from '@/constants/rutas';
import { translate } from '@/data/fixture-transalations';
import { getDateMMddHHmm } from '@/helpers/datetime-helper';
import HomeIcon from '@mui/icons-material/Home';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Link from 'next/link';

const styles = {
    width: 15,
    height: 15,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const DrawBadge = () => {
    return (
        <Box {...styles} backgroundColor="#ff8c00">
            <Typography color={'#fff'} fontSize={10}>
                E
            </Typography>
        </Box>
    );
};

const WinnerBadge = () => {
    return (
        <Box {...styles} backgroundColor="#1a572e">
            <Typography color={'#fff'} fontSize={10}>
                V
            </Typography>
        </Box>
    );
};

const LoserBadge = () => {
    return (
        <Box {...styles} backgroundColor="#943434">
            <Typography color={'#fff'} fontSize={10}>
                D
            </Typography>
        </Box>
    );
};

const IsWinner = ({ homeGoals, awayGoals, isHome }) => {
    if (!homeGoals && !awayGoals && homeGoals !== 0 && awayGoals !== 0) return null;

    if (homeGoals === awayGoals) {
        return <DrawBadge />;
    }

    if (isHome) {
        return homeGoals > awayGoals ? <WinnerBadge isHome={isHome} /> : <LoserBadge isHome={isHome} />;
    }
    return awayGoals > homeGoals ? <WinnerBadge isHome={isHome} /> : <LoserBadge isHome={isHome} />;
};

const UltimosPartidoCard = ({ fixture, equipoId, isLast }) => {
    const theme = useTheme();
    const isHome = fixture.home.id === equipoId;
    const rivalTeam = isHome ? fixture.away : fixture.home;
    const rivalImage = getTeamImageById(rivalTeam.id, 20);
    const competitionImage = getCompetitionImage(fixture.league);
    const teamGoals = isHome ? fixture.home.goals : fixture.away.goals;
    const rivalGoals = isHome ? fixture.away.goals : fixture.home.goals;

    return (
        <Box
            component={Link}
            href={`${Rutas.PARTIDO}/${fixture.id}/alineaciones-probables`}
            sx={{
                flex: 1,
                minWidth: 0,
                height: 120,
                px: 0.5,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRight: isLast ? 'none' : `1px solid ${theme.palette.divider}`,
                textDecoration: 'none',
                backgroundColor: theme.palette.background.default,
                transition: 'background-color 0.2s',
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
            }}
        >
            {/* Top: Competición y local/visitante */}
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box display="flex" alignItems="center" gap={0.5} px={0.5}>
                    <CustomImageComponent
                        src={competitionImage}
                        alt={fixture.league}
                        width={12}
                        height={12}
                    />
                    <Typography fontSize={12} color="text.secondary">
                        {translate(fixture.round, true)}
                    </Typography>
                </Box>
                {isHome ? (
                    <HomeIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />
                ) : (
                    <FlightTakeoffIcon sx={{ fontSize: 14, color: theme.palette.text.primary }} />
                )}
            </Box>

            {/* Middle: Escudo y nombre */}
            <CustomEquipoWithTooltip
                teamId={rivalTeam.id}
                src={rivalImage}
                alt={rivalTeam.name}
                width={24}
                height={24}
            />
            <Typography
                fontSize={12}
                textAlign="center"
                fontWeight={500}
                sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                }}
                title={rivalTeam.name}
            >
                {rivalTeam.name}
            </Typography>

            {/* Bottom: Fecha */}
            <Typography fontSize={11} color="text.secondary" textAlign="center" noWrap>
                {getDateMMddHHmm(fixture.date)}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontSize={12} color="text.secondary">
                    {teamGoals}-{rivalGoals}
                </Typography>
                <IsWinner homeGoals={fixture.home?.goals} awayGoals={fixture.away?.goals} isHome={isHome} />
            </Box>
        </Box>
    );
};

const UltimosPartidosEquipo = ({ equipo }) => {
    const theme = useTheme();
    const proximosPartidos = equipo?.last ?? [];

    if (!proximosPartidos.length) return null;

    const equipoImage = getTeamImageById(equipo.id, 20);
    const maxPartidos = Math.min(proximosPartidos.length, 5);

    // Título compuesto con escudo + nombre + texto
    const customTitle = (
        <Box display="flex" alignItems="center" gap={1}>
            <CustomEquipoWithTooltip
                teamId={equipo.id}
                src={equipoImage}
                alt={equipo.name}
                width={20}
                height={20}
            />
            <Typography component="span" fontSize={14} fontWeight={500}>
                {equipo.name}
            </Typography>
            <Typography component="span" fontSize={14}>
                -
            </Typography>
            <Typography component="span" fontSize={14}>
                Últimos partidos
            </Typography>
        </Box>
    );

    return (
        <CustomSectionWrapper title={customTitle} color={theme.palette.primary.main} mb={2}>
            {/* Partidos */}
            <Box display="flex" width="100%">
                {proximosPartidos.slice(0, maxPartidos).map((fixture, index) => (
                    <UltimosPartidoCard
                        key={`${fixture.id}-${index}`}
                        fixture={fixture}
                        equipoId={equipo.id}
                        isLast={index === maxPartidos - 1}
                    />
                ))}
            </Box>
        </CustomSectionWrapper>
    );
};

export default UltimosPartidosEquipo;
